import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import "./InterdicoesCimPage.css";
import InterdicaoNovaModal from "./popup/interdicao-nova/InterdicaoNovaModal";
import InterdicaoDetalhesModal from "./popup/interdicao-detalhes/InterdicaoDetalhesModal";
import InterdicaoListaViaCard from "./lista/tipos/InterdicaoListaViaCard";
import InterdicaoListaVagoesCard from "./lista/tipos/InterdicaoListaVagoesCard";
import InterdicaoListaLocomotivasCard from "./lista/tipos/InterdicaoListaLocomotivasCard";
import InterdicaoListaToCard from "./lista/tipos/InterdicaoListaToCard";
import {
  cadastrarInterdicaoCim,
  finalizarInterdicaoCim,
  listarInterdicoesCim,
  salvarAcompanhamentoInterdicaoCim,
} from "./interdicoesCim.api";
import { listarSecoesBloqueioDistinct } from "../bases/secao-bloqueio/secaoBloqueio.api";
import { listarMotivosVia } from "../bases/motivos/via/motivosVia.api";
import { listarMotivosLocomotivas } from "../bases/motivos/locomotivas/motivosLocomotivas.api";
import { listarMotivosTo } from "../bases/motivos/to/motivosTo.api";
import { listarMotivosVagoes } from "../bases/motivos/vagoes/motivosVagoes.api";
import { listarPostosOrigem } from "../bases/postos-origem/postosOrigem.api";
import { listarTiposOperacao } from "../bases/tipos-operacao/tiposOperacao.api";
import {
  RT,
  connectRealtime,
  joinRoom,
  leaveRoom,
  makeCimInterdicoesRoom,
  off as offRealtime,
  on as onRealtime,
} from "../../../api/realtime";
import {
  FILTER_ALL,
  MOTIVO_OPTIONS,
  TIPO_INTERDICAO_OPTIONS,
  pad2,
  toTodayYMD,
  parseYMD,
  toYMD,
  toBRDate,
  normalizeText,
  makeInitialInterdicaoForm,
} from "../core/cimInterdicoes.utils";

const DATE_WEEKDAYS = ["D", "S", "T", "Q", "Q", "S", "S"];
const CIM_STATUS_OPTIONS = [
  "Aguardando CCO",
  "Aguardando Finalização CCO",
  "Aprovado CCO",
  "Em andamento",
  "Finalizado",
];

function addMonths(date, months) {
  return new Date(date.getFullYear(), date.getMonth() + months, 1, 12, 0, 0);
}

function monthLabel(date) {
  const label = new Intl.DateTimeFormat("pt-BR", {
    month: "long",
    year: "numeric",
  }).format(date);
  return label.charAt(0).toUpperCase() + label.slice(1);
}

function toBRDateTime(value, hour) {
  if (!value) return "";
  return hour ?`${toBRDate(value)} \u00e0s ${hour}` : toBRDate(value);
}

function getMonthRange(referenceYmd = toTodayYMD()) {
  const base = parseYMD(referenceYmd) || parseYMD(toTodayYMD()) || new Date();
  const start = new Date(base.getFullYear(), base.getMonth(), 1, 12, 0, 0);
  const end = new Date(base.getFullYear(), base.getMonth() + 1, 0, 12, 0, 0);
  return {
    start: toYMD(start),
    end: toYMD(end),
  };
}

function parseDateTimeValue(dateValue, timeValue = "", fallbackTimestamp = null) {
  if (fallbackTimestamp) {
    const fallbackDate = new Date(fallbackTimestamp);
    if (!Number.isNaN(fallbackDate.getTime())) return fallbackDate;
  }

  if (!dateValue) return null;
  const parsedDate = parseYMD(dateValue);
  if (!parsedDate) return null;

  const [rawHour = "0", rawMinute = "0", rawSecond = "0"] = String(timeValue || "00:00:00").split(":");
  const hour = Number.parseInt(rawHour, 10);
  const minute = Number.parseInt(rawMinute, 10);
  const second = Number.parseInt(rawSecond, 10);

  parsedDate.setHours(
    Number.isFinite(hour) ?hour : 0,
    Number.isFinite(minute) ?minute : 0,
    Number.isFinite(second) ?second : 0,
    0
  );

  return parsedDate;
}

function isFinalizadaRecente(item, now = new Date()) {
  if (item.status !== "Finalizado") return true;

  const finalizedAt = parseDateTimeValue(
    null,
    "",
    item.finalizadoEm || item.atualizadoEm || item.criadoEm || null
  );

  if (!finalizedAt) return false;
  return now.getTime() - finalizedAt.getTime() <= 24 * 60 * 60 * 1000;
}

function intersectsPeriod(item, start, end) {
  const itemStart = item.dataInicio || "";
  const itemEnd = item.dataFim || itemStart;

  if (start && itemEnd < start) return false;
  if (end && itemStart > end) return false;
  return true;
}

function buildMonthGrid(viewDate) {
  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const monthStart = new Date(year, month, 1, 12, 0, 0);
  const gridStart = new Date(monthStart);
  gridStart.setDate(monthStart.getDate() - monthStart.getDay());

  return Array.from({ length: 42 }, (_, index) => {
    const day = new Date(gridStart);
    day.setDate(gridStart.getDate() + index);
    return day;
  });
}

function parseKmTrabalhoValue(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return null;

  const compact = raw.replace(/\s+/g, "");

  if (/^\d+\+\d{1,3}$/.test(compact)) {
    const [km, meters] = compact.split("+");
    return Number.parseInt(km, 10) + Number.parseInt(meters.padEnd(3, "0").slice(0, 3), 10) / 1000;
  }

  if (/^\d+[.,]\d{1,3}$/.test(compact)) {
    return Number.parseFloat(compact.replace(",", "."));
  }

  if (/^\d+$/.test(compact)) {
    if (compact.length <= 3) return Number.parseInt(compact, 10);
    const km = compact.slice(0, -3) || "0";
    const meters = compact.slice(-3);
    return Number.parseInt(km, 10) + Number.parseInt(meters, 10) / 1000;
  }

  return Number.NaN;
}

function formatKmFaixa(value) {
  const parsed = Number.parseFloat(value);
  if (!Number.isFinite(parsed)) return "-";
  return parsed.toFixed(3).replace(".", ",");
}

function mergeKmRanges(rows = []) {
  const sorted = rows
    .map((row) => ({
      start: Number.parseFloat(row?.km_inicial),
      end: Number.parseFloat(row?.km_final),
    }))
    .filter((range) => Number.isFinite(range.start) && Number.isFinite(range.end))
    .sort((left, right) => {
      if (left.start !== right.start) return left.start - right.start;
      return left.end - right.end;
    });

  return sorted.reduce((acc, range) => {
    const current = acc[acc.length - 1];
    if (!current) {
      acc.push({ ...range });
      return acc;
    }

    if (range.start <= current.end + 0.001) {
      current.end = Math.max(current.end, range.end);
      return acc;
    }

    acc.push({ ...range });
    return acc;
  }, []);
}

function isKmDentroDasFaixas(kmValue, ranges = []) {
  if (!Number.isFinite(kmValue)) return false;
  return ranges.some((range) => kmValue >= range.start - 0.0001 && kmValue <= range.end + 0.0001);
}

const TIPO_FIELD_CONFIG = {
  VIA: { sbField: "sb", kmField: "kmInicial", subField: "sub" },
  TO: { sbField: "toSb", kmField: "toKm", subField: null },
  LOCOMOTIVAS: { sbField: "locomotivasSbParada", kmField: "locomotivasKm", subField: null },
  "VAGÕES": { sbField: "vagoesSbParada", kmField: "vagoesKm", subField: null },
};

function getTipoFieldConfig(tipo) {
  return TIPO_FIELD_CONFIG[tipo] || TIPO_FIELD_CONFIG.VIA;
}

function mapApiInterdicaoToUi(row = {}) {
  const tipoInterdicao = row?.tipoInterdicao || row?.tipo_interdicao || "";
  const detalhesTipo =
    row?.detalhesTipo && typeof row.detalhesTipo === "object" ?row.detalhesTipo : {};
  const temPrevisao =
    row?.temPrevisao === true ||
    row?.temPrevisao === 1 ||
    row?.tem_previsao === 1 ||
    row?.tem_previsao === true;

  return {
    id: row?.id || `row-${Date.now()}`,
    codigoUnico: row?.codigoUnico || row?.codigo_unico || "",
    tipoInterdicao,
    mesaCco: row?.mesaCco || row?.mesa_cco || detalhesTipo.mesaCco || "",
    identificador: row?.identificador || detalhesTipo.identificador || "",
    sb:
      row?.sb ||
      row?.secaodebloqueio ||
      row?.secao_bloqueio ||
      detalhesTipo.sb ||
      detalhesTipo.sbParada ||
      "",
    sub: row?.sub != null ?String(row.sub) : detalhesTipo.sub || "",
    kmInicial: row?.kmInicial || row?.km || row?.km_inicial || detalhesTipo.km || "",
    kmFinal: row?.kmFinal || row?.km_final || "",
    dataInicio: row?.dataInicio || row?.data_inicio || "",
    horaInicio: row?.horaInicio || row?.hora_inicio || "",
    dataFim: row?.dataFim || row?.data_fim || "",
    horaFim: row?.horaFim || row?.hora_fim || "",
    motivo: row?.motivo || detalhesTipo.motivo || "",
    observacao: row?.observacao || detalhesTipo.observacao || "",
    acionamento: row?.acionamento || detalhesTipo.acionamento || "",
    nivelAcionamento:
      row?.nivelAcionamento ||
      row?.nivel_acionamento ||
      detalhesTipo.nivelAcionamento ||
      "",
    trem: row?.trem || detalhesTipo.trem || "",
    ativo: row?.ativo || row?.ativo_recurso || detalhesTipo.ativo || detalhesTipo.numeroAtivo || "",
    frota: row?.frota || detalhesTipo.frota || "",
    tipoOperacao: row?.tipoOperacao || row?.tipo_operacao || detalhesTipo.tipoOperacao || "",
    postoOrigemSos:
      row?.postoOrigemSos || row?.posto_origem_sos || detalhesTipo.postoOrigemSos || "",
    mecanicoAcionado:
      row?.mecanicoAcionado ||
      row?.mecanico_acionado ||
      detalhesTipo.mecanicoAcionado ||
      "",
    temPrevisao,
    coordenada: row?.coordenada || "",
    status: row?.status || (tipoInterdicao === "VIA" ?"Aguardando CCO" : "Em andamento"),
    numeroInterdicao: row?.numeroInterdicao || row?.numero_interdicao || "",
    sbInterdicao: row?.sbInterdicao || row?.sb_interdicao || "",
    finalizadoEm: row?.finalizadoEm || row?.finalizado_em || "",
    criadoEm: row?.criadoEm || row?.criado_em || "",
    atualizadoEm: row?.atualizadoEm || row?.atualizado_em || "",
    detalhesTipo,
    historicoStatus: Array.isArray(row?.historicoStatus) ?row.historicoStatus : [],
  };
}


function InterdDateDropdown({
  value,
  onChange,
  minDate = null,
  maxDate = null,
  panelAlign = "left",
  ariaLabel = "Selecionar data",
}) {
  const rootRef = useRef(null);
  const selectedDate = parseYMD(value) || parseYMD(toTodayYMD()) || new Date();
  const [open, setOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState(
    () => new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1, 12, 0, 0)
  );

  useEffect(() => {
    if (!open) {
      setViewMonth(new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1, 12, 0, 0));
    }
  }, [open, value]);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  const calendarDays = useMemo(() => buildMonthGrid(viewMonth), [viewMonth]);
  const today = toTodayYMD();
  const currentMonth = viewMonth.getMonth();
  const previousMonthBoundary = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), 0, 12, 0, 0);
  const nextMonthBoundary = new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1, 12, 0, 0);

  const monthPrevDisabled =
    !!minDate && toYMD(previousMonthBoundary) < minDate;
  const monthNextDisabled =
    !!maxDate && toYMD(nextMonthBoundary) > maxDate;

  const handlePick = (ymd) => {
    if (minDate && ymd < minDate) return;
    if (maxDate && ymd > maxDate) return;
    onChange(ymd);
    setOpen(false);
  };

  return (
    <div
      className={`interd-dateRoot interd-dateRoot--${panelAlign}${open ?" is-open" : ""}`}
      ref={rootRef}
    >
      <button
        type="button"
        className={`interd-dateBtn ${open ?"open" : ""}`}
        onClick={() => setOpen((current) => !current)}
        aria-label={ariaLabel}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <span className="interd-dateValue">{toBRDate(value)}</span>
      </button>

      {open ?(
        <div className="interd-datePanel" role="dialog" aria-label={ariaLabel}>
          <div className="interd-datePanelHead">
            <button
              type="button"
              className="interd-dateNav"
              onClick={() => !monthPrevDisabled && setViewMonth((current) => addMonths(current, -1))}
              disabled={monthPrevDisabled}
              aria-label={"M\u00eas anterior"}
            >
              {"<"}
            </button>
            <div className="interd-datePanelTitle">{monthLabel(viewMonth)}</div>
            <button
              type="button"
              className="interd-dateNav"
              onClick={() => !monthNextDisabled && setViewMonth((current) => addMonths(current, 1))}
              disabled={monthNextDisabled}
              aria-label={"Pr\u00f3ximo m\u00eas"}
            >
              {">"}
            </button>
          </div>

          <div className="interd-dateWeekdays">
            {DATE_WEEKDAYS.map((weekday, index) => (
              <span key={`${weekday}-${index}`} className="interd-dateWeekday">
                {weekday}
              </span>
            ))}
          </div>

          <div className="interd-dateGrid">
            {calendarDays.map((day) => {
              const dayKey = `${day.getFullYear()}-${pad2(day.getMonth() + 1)}-${pad2(day.getDate())}`;
              const isOutside = day.getMonth() !== currentMonth;
              const isSelected = dayKey === value;
              const isToday = dayKey === today;
              const isDisabled =
                (minDate && dayKey < minDate) || (maxDate && dayKey > maxDate);

              return (
                <button
                  type="button"
                  key={dayKey}
                  className={[
                    "interd-dateDay",
                    isOutside ?"is-outside" : "",
                    isSelected ?"is-selected" : "",
                    isToday ?"is-today" : "",
                    isDisabled ?"is-disabled" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  onClick={() => handlePick(dayKey)}
                  disabled={isDisabled}
                  aria-pressed={isSelected}
                >
                  {day.getDate()}
                </button>
              );
            })}
          </div>

          <div className="interd-datePanelFoot">
            <button type="button" className="interd-dateAction" onClick={() => handlePick(today)}>
              Hoje
            </button>
            <button
              type="button"
              className="interd-dateAction interd-dateActionGhost"
              onClick={() => setOpen(false)}
            >
              Fechar
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function InterdSingleSelect({
  value,
  onChange,
  options,
  title,
  placeholder,
  searchable = false,
  searchPlaceholder = "Buscar...",
}) {
  const rootRef = useRef(null);
  const searchRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!open) {
      setQuery("");
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    if (searchable) {
      window.setTimeout(() => searchRef.current?.focus(), 0);
    }

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, searchable]);

  const selectedOption = options.find((option) => option.value === value);
  const buttonLabel = selectedOption?.label || placeholder;
  const filteredOptions = options.filter((option) =>
    normalizeText(option.label).includes(normalizeText(query))
  );

  return (
    <div className="interd-selectRoot" ref={rootRef}>
      <button
        type="button"
        className={`interd-selectBtn ${open ?"is-open" : ""}`}
        onClick={() => setOpen((current) => !current)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={`interd-selectBtnLabel ${selectedOption ?"" : "is-placeholder"}`}>
          {buttonLabel}
        </span>
      </button>

      {open ?(
        <div className="interd-selectPanel" role="listbox" aria-label={title}>
          <div className="interd-selectPanelHead">
            <div className="interd-selectPanelTitle">{title}</div>
            <button
              type="button"
              className="interd-selectClear"
              onClick={() => {
                onChange(FILTER_ALL);
                setOpen(false);
              }}
            >
              Limpar
            </button>
          </div>

          {searchable ?(
            <div className="interd-selectSearchWrap">
              <input
                ref={searchRef}
                type="text"
                className="interd-selectSearch"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={searchPlaceholder}
              />
            </div>
          ) : null}

          <div className="interd-selectList">
            {filteredOptions.map((option) => {
              const isSelected = option.value === value;
              return (
                <button
                  type="button"
                  key={option.value}
                  className={`interd-selectItem ${isSelected ?"is-selected" : ""}`}
                  onClick={() => {
                    onChange(option.value);
                    setOpen(false);
                  }}
                  aria-pressed={isSelected}
                >
                  <span className="interd-selectItemLabel">{option.label}</span>
                </button>
              );
            })}

            {!filteredOptions.length ?(
              <div className="interd-selectEmpty">Nenhum resultado encontrado.</div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function renderInterdicaoListaCard(item, onOpen) {
  switch (item.tipoInterdicao) {
    case "VAGÕES":
      return <InterdicaoListaVagoesCard key={item.id} item={item} onOpen={onOpen} />;
    case "LOCOMOTIVAS":
      return <InterdicaoListaLocomotivasCard key={item.id} item={item} onOpen={onOpen} />;
    case "TO":
      return <InterdicaoListaToCard key={item.id} item={item} onOpen={onOpen} />;
    case "VIA":
    default:
      return <InterdicaoListaViaCard key={item.id} item={item} onOpen={onOpen} />;
  }
}

export default function InterdicoesCimPage({ onBack }) {
  const currentMonthRange = useMemo(() => getMonthRange(), []);
  const [interdicoes, setInterdicoes] = useState([]);
  const [sbBaseRows, setSbBaseRows] = useState([]);
  const [sbBaseOptions, setSbBaseOptions] = useState([]);
  const [motivosViaOptions, setMotivosViaOptions] = useState([]);
  const [motivosLocomotivasOptions, setMotivosLocomotivasOptions] = useState([]);
  const [motivosToOptions, setMotivosToOptions] = useState([]);
  const [motivosVagoesOptions, setMotivosVagoesOptions] = useState([]);
  const [postosOrigemOptions, setPostosOrigemOptions] = useState([]);
  const [tiposOperacaoOptions, setTiposOperacaoOptions] = useState([]);
  const [novaOpen, setNovaOpen] = useState(false);
  const [interdicaoDetalhes, setInterdicaoDetalhes] = useState(null);
  const [form, setForm] = useState(() => makeInitialInterdicaoForm());
  const [saveError, setSaveError] = useState("");
  const [savingNova, setSavingNova] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [savingDetalhes, setSavingDetalhes] = useState(false);
  const [savingDetalhesAction, setSavingDetalhesAction] = useState("");
  const [saveDetalhesError, setSaveDetalhesError] = useState("");
  const sidebarRef = useRef(null);
  const summaryRef = useRef(null);
  const realtimeReloadTimerRef = useRef(null);

  const [busca, setBusca] = useState("");
  const [filtroInicio, setFiltroInicio] = useState(currentMonthRange.start);
  const [filtroFim, setFiltroFim] = useState(currentMonthRange.end);
  const [filtroTipo, setFiltroTipo] = useState(FILTER_ALL);
  const [filtroStatus, setFiltroStatus] = useState(FILTER_ALL);
  const [filtroMesaCco, setFiltroMesaCco] = useState(FILTER_ALL);
  const [mostrarFinalizadasAntigas, setMostrarFinalizadasAntigas] = useState(false);

  useEffect(() => {
    if (!novaOpen && !interdicaoDetalhes) return undefined;

    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [interdicaoDetalhes, novaOpen]);

  const carregarInterdicoes = useCallback(async () => {
    try {
      setLoadError("");
      const result = await listarInterdicoesCim({ limit: 5000, offset: 0 });
      const rows = Array.isArray(result.rows) ?result.rows.map(mapApiInterdicaoToUi) : [];
      setInterdicoes(rows);
    } catch (err) {
      setLoadError(err?.message || "Não foi possível carregar as interdições do banco.");
    }
  }, []);

  useEffect(() => {
    carregarInterdicoes();
  }, [carregarInterdicoes]);

  useEffect(() => {
    const room = makeCimInterdicoesRoom();

    const handleRealtimeChanged = () => {
      if (realtimeReloadTimerRef.current) {
        window.clearTimeout(realtimeReloadTimerRef.current);
      }

      realtimeReloadTimerRef.current = window.setTimeout(() => {
        carregarInterdicoes();
      }, 120);
    };

    connectRealtime().catch(() => {});
    if (room) joinRoom(room);
    onRealtime(RT.CIM_INTERDICOES_CHANGED, handleRealtimeChanged);

    return () => {
      offRealtime(RT.CIM_INTERDICOES_CHANGED, handleRealtimeChanged);
      if (room) leaveRoom(room);
      if (realtimeReloadTimerRef.current) {
        window.clearTimeout(realtimeReloadTimerRef.current);
        realtimeReloadTimerRef.current = null;
      }
    };
  }, [carregarInterdicoes]);

  useEffect(() => {
    let active = true;

    const carregarSecoesBloqueio = async () => {
      try {
        const result = await listarSecoesBloqueioDistinct({
          ativo: 1,
          limit: 5000,
          offset: 0,
        });

        if (!active) return;

        const seen = new Set();
        const collator = new Intl.Collator("pt-BR", {
          sensitivity: "base",
          numeric: true,
        });

        const options = (result.rows || [])
          .map((row) => String(row?.secao_bloqueio || row?.secaodebloqueio || row?.sb || "").trim())
          .filter(Boolean)
          .filter((value) => {
            const key = normalizeText(value);
            if (!key || seen.has(key)) return false;
            seen.add(key);
            return true;
          })
          .sort((left, right) => collator.compare(left, right))
          .map((value) => ({ value, label: value }));

        setSbBaseRows(Array.isArray(result.rows) ?result.rows : []);
        setSbBaseOptions(options);
      } catch {
        if (active) {
          setSbBaseRows([]);
          setSbBaseOptions([]);
        }
      }
    };

    carregarSecoesBloqueio();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;

    const carregarTiposOperacao = async () => {
      try {
        const result = await listarTiposOperacao({
          ativo: 1,
          limit: 5000,
          offset: 0,
        });

        if (!active) return;

        const collator = new Intl.Collator("pt-BR", {
          sensitivity: "base",
          numeric: true,
        });
        const seen = new Set();

        const options = (result.rows || [])
          .map((row) => String(row?.tipo_operacao || row?.tipoOperacao || row?.motivo || "").trim())
          .filter(Boolean)
          .filter((value) => {
            const key = normalizeText(value);
            if (!key || seen.has(key)) return false;
            seen.add(key);
            return true;
          })
          .sort((left, right) => collator.compare(left, right))
          .map((value) => ({ value, label: value }));

        setTiposOperacaoOptions(options);
      } catch {
        if (active) setTiposOperacaoOptions([]);
      }
    };

    carregarTiposOperacao();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;

    const carregarPostosOrigem = async () => {
      try {
        const result = await listarPostosOrigem({
          ativo: 1,
          limit: 5000,
          offset: 0,
        });

        if (!active) return;

        const collator = new Intl.Collator("pt-BR", {
          sensitivity: "base",
          numeric: true,
        });
        const seen = new Set();

        const options = (result.rows || [])
          .map((row) => String(row?.posto_origem || row?.postoOrigem || row?.motivo || "").trim())
          .filter(Boolean)
          .filter((value) => {
            const key = normalizeText(value);
            if (!key || seen.has(key)) return false;
            seen.add(key);
            return true;
          })
          .sort((left, right) => collator.compare(left, right))
          .map((value) => ({ value, label: value }));

        setPostosOrigemOptions(options);
      } catch {
        if (active) setPostosOrigemOptions([]);
      }
    };

    carregarPostosOrigem();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;

    const carregarMotivosVia = async () => {
      try {
        const result = await listarMotivosVia({
          ativo: 1,
          limit: 5000,
          offset: 0,
        });

        if (!active) return;

        const collator = new Intl.Collator("pt-BR", {
          sensitivity: "base",
          numeric: true,
        });
        const seen = new Set();

        const options = (result.rows || [])
          .map((row) => String(row?.motivo || "").trim())
          .filter(Boolean)
          .filter((value) => {
            const key = normalizeText(value);
            if (!key || seen.has(key)) return false;
            seen.add(key);
            return true;
          })
          .sort((left, right) => collator.compare(left, right))
          .map((value) => ({ value, label: value }));

        setMotivosViaOptions(options);
      } catch {
        if (active) setMotivosViaOptions([]);
      }
    };

    carregarMotivosVia();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;

    const carregarMotivosLocomotivas = async () => {
      try {
        const result = await listarMotivosLocomotivas({
          ativo: 1,
          limit: 5000,
          offset: 0,
        });

        if (!active) return;

        const collator = new Intl.Collator("pt-BR", {
          sensitivity: "base",
          numeric: true,
        });
        const seen = new Set();

        const options = (result.rows || [])
          .map((row) => String(row?.motivo || "").trim())
          .filter(Boolean)
          .filter((value) => {
            const key = normalizeText(value);
            if (!key || seen.has(key)) return false;
            seen.add(key);
            return true;
          })
          .sort((left, right) => collator.compare(left, right))
          .map((value) => ({ value, label: value }));

        setMotivosLocomotivasOptions(options);
      } catch {
        if (active) setMotivosLocomotivasOptions([]);
      }
    };

    carregarMotivosLocomotivas();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;

    const carregarMotivosTo = async () => {
      try {
        const result = await listarMotivosTo({
          ativo: 1,
          limit: 5000,
          offset: 0,
        });

        if (!active) return;

        const collator = new Intl.Collator("pt-BR", {
          sensitivity: "base",
          numeric: true,
        });
        const seen = new Set();

        const options = (result.rows || [])
          .map((row) => String(row?.motivo || "").trim())
          .filter(Boolean)
          .filter((value) => {
            const key = normalizeText(value);
            if (!key || seen.has(key)) return false;
            seen.add(key);
            return true;
          })
          .sort((left, right) => collator.compare(left, right))
          .map((value) => ({ value, label: value }));

        setMotivosToOptions(options);
      } catch {
        if (active) setMotivosToOptions([]);
      }
    };

    carregarMotivosTo();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;

    const carregarMotivosVagoes = async () => {
      try {
        const result = await listarMotivosVagoes({
          ativo: 1,
          limit: 5000,
          offset: 0,
        });

        if (!active) return;

        const collator = new Intl.Collator("pt-BR", {
          sensitivity: "base",
          numeric: true,
        });
        const seen = new Set();

        const options = (result.rows || [])
          .map((row) => String(row?.motivo || "").trim())
          .filter(Boolean)
          .filter((value) => {
            const key = normalizeText(value);
            if (!key || seen.has(key)) return false;
            seen.add(key);
            return true;
          })
          .sort((left, right) => collator.compare(left, right))
          .map((value) => ({ value, label: value }));

        setMotivosVagoesOptions(options);
      } catch {
        if (active) setMotivosVagoesOptions([]);
      }
    };

    carregarMotivosVagoes();

    return () => {
      active = false;
    };
  }, []);

  const sbToMesaCcoMap = useMemo(() => {
    const grouped = new Map();

    for (const row of sbBaseRows) {
      const sb = String(row?.secao_bloqueio || row?.secaodebloqueio || row?.sb || "").trim();
      const mesa = String(row?.mesa_cco || row?.mesaCco || "").trim();
      if (!sb || !mesa) continue;

      if (!grouped.has(sb)) grouped.set(sb, new Set());
      grouped.get(sb).add(mesa);
    }

    const map = new Map();
    grouped.forEach((mesas, sb) => {
      map.set(sb, [...mesas]);
    });
    return map;
  }, [sbBaseRows]);

  const mesaCcoFilterOptions = useMemo(() => {
    const collator = new Intl.Collator("pt-BR", {
      sensitivity: "base",
      numeric: true,
    });
    const seen = new Set();

    return [
      { value: FILTER_ALL, label: "Todas as mesas" },
      ...sbBaseRows
        .map((row) => String(row?.mesa_cco || row?.mesaCco || "").trim())
        .filter(Boolean)
        .filter((value) => {
          const key = normalizeText(value);
          if (!key || seen.has(key)) return false;
          seen.add(key);
          return true;
        })
        .sort((left, right) => collator.compare(left, right))
        .map((value) => ({ value, label: value })),
    ];
  }, [sbBaseRows]);

  const baseFilteredInterdicoes = useMemo(() => {
    const query = normalizeText(busca);

    return interdicoes
      .filter((item) => {
        const matchesQuery =
          !query ||
          normalizeText(
            [
              item.codigoUnico,
              item.identificador,
              item.sb,
              item.sub,
              item.motivo,
              item.observacao,
              item.coordenada,
              item.acionamento,
              item.mesaCco,
              item.kmInicial,
              item.kmFinal,
            ].join(" ")
          ).includes(query);

        const mesasDaSb = sbToMesaCcoMap.get(String(item.sb || "").trim()) || [];
        const mesaAtual = String(item.mesaCco || "").trim();
        const matchesTipo =
          filtroTipo === FILTER_ALL || item.tipoInterdicao === filtroTipo;
        const matchesStatus =
          filtroStatus === FILTER_ALL || item.status === filtroStatus;
        const matchesMesaCco =
          filtroMesaCco === FILTER_ALL ||
          normalizeText(mesaAtual) === normalizeText(filtroMesaCco) ||
          mesasDaSb.some((mesa) => normalizeText(mesa) === normalizeText(filtroMesaCco));
        const matchesPeriodo = intersectsPeriod(item, filtroInicio, filtroFim);

        return (
          matchesQuery &&
          matchesTipo &&
          matchesStatus &&
          matchesMesaCco &&
          matchesPeriodo
        );
      });
  }, [
    busca,
    filtroFim,
    filtroInicio,
    filtroMesaCco,
    filtroStatus,
    filtroTipo,
    interdicoes,
    sbToMesaCcoMap,
  ]);

  const finalizadasAntigasOcultas = useMemo(
    () =>
      baseFilteredInterdicoes.filter(
        (item) => item.status === "Finalizado" && !isFinalizadaRecente(item)
      ).length,
    [baseFilteredInterdicoes]
  );

  const filteredInterdicoes = useMemo(() => {
    return baseFilteredInterdicoes
      .filter((item) => mostrarFinalizadasAntigas || isFinalizadaRecente(item))
      .sort((left, right) => {
        const rightId = Number.parseInt(String(right.id ?? ""), 10);
        const leftId = Number.parseInt(String(left.id ?? ""), 10);

        if (Number.isFinite(rightId) && Number.isFinite(leftId) && rightId !== leftId) {
          return rightId - leftId;
        }
        if (left.dataInicio !== right.dataInicio) {
          return String(right.dataInicio).localeCompare(String(left.dataInicio));
        }
        if (left.horaInicio !== right.horaInicio) {
          return String(right.horaInicio || "").localeCompare(String(left.horaInicio || ""));
        }
        return String(right.codigoUnico || right.identificador || right.id).localeCompare(
          String(left.codigoUnico || left.identificador || left.id)
        );
      });
  }, [baseFilteredInterdicoes, mostrarFinalizadasAntigas]);

  const resumoSidebar = useMemo(() => {
    const total = filteredInterdicoes.length;
    const aguardandoCco = filteredInterdicoes.filter(
      (item) => item.status === "Aguardando CCO"
    ).length;
    const aguardandoFinalizacaoCco = filteredInterdicoes.filter(
      (item) => item.status === "Aguardando Finalização CCO"
    ).length;
    const emAndamento = filteredInterdicoes.filter(
      (item) => item.status === "Em andamento"
    ).length;
    const finalizadas = filteredInterdicoes.filter(
      (item) => item.status === "Finalizado"
    ).length;

    return {
      total,
      aguardandoCco,
      aguardandoFinalizacaoCco,
      emAndamento,
      finalizadas,
      periodo: `${toBRDate(filtroInicio)} a ${toBRDate(filtroFim)}`,
    };
  }, [filteredInterdicoes, filtroFim, filtroInicio]);

  useLayoutEffect(() => {
    const apply = () => {
      const el = sidebarRef.current;
      if (!el) return;

      const h = el.clientHeight || window.innerHeight;
      const BASE = 1080;
      const s = h / BASE;
      const LIFT = 26 * s;

      const map = {
        "--y-menu": 46 * s,
        "--y-title-data": (216 - 26) * s - LIFT,
        "--y-data": 216 * s - LIFT,
        "--y-title-busca": (304 - 26) * s - LIFT,
        "--y-busca": 304 * s - LIFT,
        "--y-title-mesa-cco": (392 - 26) * s - LIFT,
        "--y-mesa-cco": 392 * s - LIFT,
        "--y-title-tipo": (480 - 26) * s - LIFT,
        "--y-tipo": 480 * s - LIFT,
        "--y-title-status": (568 - 26) * s - LIFT,
        "--y-status": 568 * s - LIFT,
        "--y-status-extra": 622 * s - LIFT,
      };

      const CTRL_H = 42;
      const GAP = 12;
      const SUMMARY_GAP = 18;
      const BOTTOM = 22;
      const yNova = h - BOTTOM - CTRL_H - LIFT;
      const yLimpar = yNova - GAP - CTRL_H;
      const summaryHeight = Math.max(116, Math.ceil(summaryRef.current?.offsetHeight || 0));
      const yResumo = yLimpar - SUMMARY_GAP - summaryHeight;

      Object.entries(map).forEach(([key, value]) => {
        el.style.setProperty(key, `${Math.round(value)}px`);
      });
      el.style.setProperty("--y-resumo", `${Math.round(yResumo)}px`);
      el.style.setProperty("--y-limpar", `${Math.round(yLimpar)}px`);
      el.style.setProperty("--y-nova", `${Math.round(yNova)}px`);
    };

    apply();
    window.addEventListener("resize", apply);

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ?new ResizeObserver(() => apply())
        : null;

    if (resizeObserver) {
      if (sidebarRef.current) resizeObserver.observe(sidebarRef.current);
      if (summaryRef.current) resizeObserver.observe(summaryRef.current);
    }

    return () => {
      window.removeEventListener("resize", apply);
      resizeObserver?.disconnect();
    };
  }, [resumoSidebar]);

  const statusFilterOptions = useMemo(
    () => [
      { value: FILTER_ALL, label: "Todos os status" },
      ...CIM_STATUS_OPTIONS.map((option) => ({ value: option, label: option })),
    ],
    []
  );

  const tipoFilterOptions = useMemo(
    () => [
      { value: FILTER_ALL, label: "Todos os tipos" },
      ...TIPO_INTERDICAO_OPTIONS.map((option) => ({ value: option, label: option })),
    ],
    []
  );

  const sbDropdownOptions = useMemo(() => {
    const source = sbBaseOptions;

    if (!form.sb) return source;
    if (source.some((option) => option.value === form.sb)) return source;

    return [{ value: form.sb, label: form.sb }, ...source];
  }, [form.sb, sbBaseOptions]);

  const sbToSubMap = useMemo(() => {
    const collator = new Intl.Collator("pt-BR", {
      sensitivity: "base",
      numeric: true,
    });

    const grouped = new Map();

    for (const row of sbBaseRows) {
      const sb = String(row?.secao_bloqueio || row?.secaodebloqueio || row?.sb || "").trim();
      const sub = String(row?.sub ?? "").trim();
      if (!sb || !sub) continue;

      if (!grouped.has(sb)) grouped.set(sb, new Set());
      grouped.get(sb).add(sub);
    }

    const map = new Map();
    grouped.forEach((subs, sb) => {
      const sortedSubs = [...subs].sort((left, right) => collator.compare(left, right));
      if (sortedSubs.length) {
        map.set(sb, sortedSubs[0]);
      }
    });

    return map;
  }, [sbBaseRows]);

  const sbToRangesMap = useMemo(() => {
    const grouped = new Map();

    for (const row of sbBaseRows) {
      const sb = String(row?.secao_bloqueio || row?.secaodebloqueio || row?.sb || "").trim();
      if (!sb) continue;

      if (!grouped.has(sb)) grouped.set(sb, []);
      grouped.get(sb).push(row);
    }

    const map = new Map();
    grouped.forEach((rows, sb) => {
      map.set(sb, mergeKmRanges(rows));
    });

    return map;
  }, [sbBaseRows]);

  useEffect(() => {
    if (!form.sb || form.sub?.trim()) return;

    const nextSub = sbToSubMap.get(form.sb.trim()) || "";
    if (!nextSub) return;

    setForm((current) => {
      if (current.sb !== form.sb || current.sub?.trim()) return current;
      return { ...current, sub: nextSub };
    });
  }, [form.sb, form.sub, sbToSubMap]);

  const activeTipoConfig = useMemo(() => getTipoFieldConfig(form.tipoInterdicao), [form.tipoInterdicao]);

  const activeSbValue = useMemo(
    () => String(form[activeTipoConfig.sbField] || "").trim(),
    [activeTipoConfig.sbField, form]
  );

  const activeKmValue = useMemo(
    () => String(form[activeTipoConfig.kmField] || "").trim(),
    [activeTipoConfig.kmField, form]
  );

  const selectedSbRanges = useMemo(
    () => (activeSbValue ?sbToRangesMap.get(activeSbValue) || [] : []),
    [activeSbValue, sbToRangesMap]
  );

  const kmTrabalhoNumber = useMemo(() => parseKmTrabalhoValue(activeKmValue), [activeKmValue]);

  const kmTrabalhoHint = useMemo(() => {
    if (!activeSbValue || !selectedSbRanges.length) return "";

    const faixaLabel = selectedSbRanges
      .slice(0, 3)
      .map((range) => `${formatKmFaixa(range.start)} até ${formatKmFaixa(range.end)}`)
      .join(" | ");

    return selectedSbRanges.length > 3
      ?`Faixas válidas desta SB: ${faixaLabel} | ...`
      : `Faixa válida desta SB: ${faixaLabel}`;
  }, [activeSbValue, selectedSbRanges]);

  const kmTrabalhoError = useMemo(() => {
    if (!activeSbValue || !activeKmValue) return "";

    if (!Number.isFinite(kmTrabalhoNumber)) {
      return "Informe um KM válido. Ex.: 125,300 ou 125.300.";
    }

    if (selectedSbRanges.length && !isKmDentroDasFaixas(kmTrabalhoNumber, selectedSbRanges)) {
      return "O KM de trabalho precisa estar dentro da faixa cadastrada para a SB selecionada.";
    }

    return "";
  }, [activeKmValue, activeSbValue, kmTrabalhoNumber, selectedSbRanges]);

  const canSubmit = useMemo(() => {
    switch (form.tipoInterdicao) {
      case "TO":
        return Boolean(
          form.toData &&
            form.toHorarioInicio &&
            form.toSb.trim() &&
            form.toKm.trim() &&
            form.toAtivo.trim() &&
            form.toAcionamento.trim() &&
            form.toNivelAcionamento.trim() &&
            form.toMotivo.trim() &&
            (form.toTemLiberacao === "nao" ||
              Boolean(form.toDataLiberacao && form.toHorarioLiberacao)) &&
            !kmTrabalhoError
        );
      case "LOCOMOTIVAS":
        return Boolean(
          form.locomotivasData &&
            form.locomotivasHorarioInicio &&
            form.locomotivasTrem.trim() &&
            form.locomotivasSbParada.trim() &&
            form.locomotivasKm.trim() &&
            form.locomotivasNumeroAtivo.trim() &&
            form.locomotivasFrota.trim() &&
            form.locomotivasTipoOperacao.trim() &&
            form.locomotivasPostoOrigemSos.trim() &&
            form.locomotivasMecanicoAcionado.trim() &&
            form.locomotivasNivelAcionamento.trim() &&
            form.locomotivasMotivo.trim() &&
            (form.locomotivasTemLiberacao === "nao" ||
              Boolean(form.locomotivasDataLiberacao && form.locomotivasHorarioLiberacao)) &&
            !kmTrabalhoError
        );
      case "VAGÕES":
        return Boolean(
          form.vagoesData &&
            form.vagoesHorarioInicio &&
            form.vagoesTrem.trim() &&
            form.vagoesSbParada.trim() &&
            form.vagoesKm.trim() &&
            form.vagoesNumeroAtivo.trim() &&
            form.vagoesFrota.trim() &&
            form.vagoesTipoOperacao.trim() &&
            form.vagoesPostoOrigemSos.trim() &&
            form.vagoesMecanicoAcionado.trim() &&
            form.vagoesNivelAcionamento.trim() &&
            form.vagoesMotivo.trim() &&
            (form.vagoesTemLiberacao === "nao" ||
              Boolean(form.vagoesDataLiberacao && form.vagoesHorarioLiberacao)) &&
            !kmTrabalhoError
        );
      case "VIA":
      default: {
        const previsaoValida =
          form.temPrevisao === "nao" || Boolean(form.dataFim && form.horaFim);
        const subAtual = form.sub.trim() || (form.sb.trim() ?sbToSubMap.get(form.sb.trim()) || "" : "");

        return Boolean(
          form.tipoInterdicao.trim() &&
            form.viaData &&
            form.viaHorarioInicio &&
            form.sb.trim() &&
            subAtual &&
            form.acionamento.trim() &&
            form.nivelAcionamento.trim() &&
            form.kmInicial.trim() &&
            form.motivo.trim() &&
            previsaoValida &&
            !kmTrabalhoError
        );
      }
    }
  }, [form, kmTrabalhoError, sbToSubMap]);

  const handleFormChange = (field) => (event) => {
    const value = event.target.value;
    if (saveError) setSaveError("");
    setForm((current) => {
      const sbFieldConfig = {
        sb: { kmField: "kmInicial", subField: "sub" },
        toSb: { kmField: "toKm", subField: null },
        locomotivasSbParada: { kmField: "locomotivasKm", subField: null },
        vagoesSbParada: { kmField: "vagoesKm", subField: null },
      };

      if (sbFieldConfig[field]) {
        const { kmField, subField } = sbFieldConfig[field];
        const nextRanges = value ?sbToRangesMap.get(value) || [] : [];
        const currentKmValue = parseKmTrabalhoValue(current[kmField]);
        const keepCurrentKm =
          value &&
          String(current[kmField] || "").trim() &&
          Number.isFinite(currentKmValue) &&
          (!nextRanges.length || isKmDentroDasFaixas(currentKmValue, nextRanges))
            ?current[kmField]
            : "";

        const nextState = {
          ...current,
          [field]: value,
          [kmField]: keepCurrentKm,
        };

        if (subField) {
          nextState[subField] = value ?sbToSubMap.get(value) || "" : "";
        }

        return nextState;
      }

      if (field === "temPrevisao") {
        return {
          ...current,
          temPrevisao: value,
          dataFim: value === "sim" ?current.dataFim : "",
          horaFim: value === "sim" ?current.horaFim : "",
        };
      }

      if (field === "toTemLiberacao") {
        return {
          ...current,
          toTemLiberacao: value,
          toDataLiberacao: value === "sim" ?current.toDataLiberacao : "",
          toHorarioLiberacao: value === "sim" ?current.toHorarioLiberacao : "",
        };
      }

      if (field === "locomotivasTemLiberacao") {
        return {
          ...current,
          locomotivasTemLiberacao: value,
          locomotivasDataLiberacao: value === "sim" ?current.locomotivasDataLiberacao : "",
          locomotivasHorarioLiberacao:
            value === "sim" ?current.locomotivasHorarioLiberacao : "",
        };
      }

      if (field === "vagoesTemLiberacao") {
        return {
          ...current,
          vagoesTemLiberacao: value,
          vagoesDataLiberacao: value === "sim" ?current.vagoesDataLiberacao : "",
          vagoesHorarioLiberacao: value === "sim" ?current.vagoesHorarioLiberacao : "",
        };
      }

      return { ...current, [field]: value };
    });
  };

  const handleFiltroInicio = (nextValue) => {
    setFiltroInicio(nextValue);
    if (nextValue > filtroFim) setFiltroFim(nextValue);
  };

  const handleFiltroFim = (nextValue) => {
    setFiltroFim(nextValue);
    if (nextValue < filtroInicio) setFiltroInicio(nextValue);
  };

  const resetCadastro = () => {
    setForm(makeInitialInterdicaoForm());
  };

  const handleCloseNova = () => {
    setSaveError("");
    resetCadastro();
    setNovaOpen(false);
  };

  const handleOpenDetalhes = (item) => {
    setSaveDetalhesError("");
    setInterdicaoDetalhes(item);
  };

  const handleCloseDetalhes = () => {
    if (savingDetalhes) return;
    setSaveDetalhesError("");
    setInterdicaoDetalhes(null);
  };

  const handleSaveDetalhes = async (id, nextValues) => {
    try {
      setSaveDetalhesError("");
      setSavingDetalhes(true);
      setSavingDetalhesAction("salvar");

      const updated = await salvarAcompanhamentoInterdicaoCim(id, nextValues);
      const updatedItem = mapApiInterdicaoToUi(updated);

      setInterdicoes((current) =>
        current.map((item) => (item.id === id ?updatedItem : item))
      );
      setInterdicaoDetalhes(updatedItem);
    } catch (err) {
      setSaveDetalhesError(err?.message || "Não foi possível salvar o acompanhamento.");
      throw err;
    } finally {
      setSavingDetalhes(false);
      setSavingDetalhesAction("");
    }
  };

  const handleFinalizarDetalhes = async (id, nextValues) => {
    try {
      setSaveDetalhesError("");
      setSavingDetalhes(true);
      setSavingDetalhesAction("finalizar");

      const updated = await finalizarInterdicaoCim(id, nextValues);
      const updatedItem = mapApiInterdicaoToUi(updated);

      setInterdicoes((current) =>
        current.map((item) => (item.id === id ?updatedItem : item))
      );
      setInterdicaoDetalhes(updatedItem);
    } catch (err) {
      setSaveDetalhesError(err?.message || "Não foi possível finalizar a interdição.");
      throw err;
    } finally {
      setSavingDetalhes(false);
      setSavingDetalhesAction("");
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (savingNova) return;
    if (!canSubmit) {
      setSaveError("Preencha os campos obrigatórios para cadastrar a interdição.");
      return;
    }

    const tipoInterdicao = form.tipoInterdicao.trim() || "VIA";
    let nextItem;

    switch (tipoInterdicao) {
      case "TO":
        nextItem = {
          tipoInterdicao,
          sb: form.toSb.trim(),
          sub: form.toSb.trim() ?sbToSubMap.get(form.toSb.trim()) || "" : "",
          kmInicial: form.toKm.trim(),
          kmFinal: "",
          dataInicio: form.toData,
          horaInicio: form.toHorarioInicio,
          dataFim: form.toTemLiberacao === "sim" ?form.toDataLiberacao : "",
          horaFim: form.toTemLiberacao === "sim" ?form.toHorarioLiberacao : "",
          motivo: form.toMotivo.trim(),
          observacao: form.toObservacao.trim(),
          acionamento: form.toAcionamento.trim(),
          nivelAcionamento: form.toNivelAcionamento.trim(),
          temPrevisao: form.toTemLiberacao === "sim",
          coordenada: "",
          numeroInterdicao: "",
          sbInterdicao: "",
          detalhesTipo: {
            tipo: "TO",
            data: form.toData,
            horarioInicio: form.toHorarioInicio,
            sb: form.toSb.trim(),
            km: form.toKm.trim(),
            ativo: form.toAtivo.trim(),
            acionamento: form.toAcionamento.trim(),
            nivelAcionamento: form.toNivelAcionamento.trim(),
            motivo: form.toMotivo.trim(),
            observacao: form.toObservacao.trim(),
            temLiberacao: form.toTemLiberacao,
            dataLiberacao: form.toTemLiberacao === "sim" ?form.toDataLiberacao : "",
            horarioLiberacao: form.toHorarioLiberacao,
          },
        };
        break;
      case "LOCOMOTIVAS":
        nextItem = {
          tipoInterdicao,
          sb: form.locomotivasSbParada.trim(),
          sub: form.locomotivasSbParada.trim()
            ?sbToSubMap.get(form.locomotivasSbParada.trim()) || ""
            : "",
          kmInicial: form.locomotivasKm.trim(),
          kmFinal: "",
          dataInicio: form.locomotivasData,
          horaInicio: form.locomotivasHorarioInicio,
          dataFim:
            form.locomotivasTemLiberacao === "sim" ?form.locomotivasDataLiberacao : "",
          horaFim:
            form.locomotivasTemLiberacao === "sim" ?form.locomotivasHorarioLiberacao : "",
          motivo: form.locomotivasMotivo.trim(),
          observacao: form.locomotivasObservacao.trim(),
          acionamento: `Trem: ${form.locomotivasTrem.trim()} | Ativo: ${form.locomotivasNumeroAtivo.trim()}`,
          nivelAcionamento: form.locomotivasNivelAcionamento.trim(),
          temPrevisao: form.locomotivasTemLiberacao === "sim",
          coordenada: "",
          numeroInterdicao: "",
          sbInterdicao: "",
          detalhesTipo: {
            tipo: "LOCOMOTIVAS",
            data: form.locomotivasData,
            horarioInicio: form.locomotivasHorarioInicio,
            trem: form.locomotivasTrem.trim(),
            sbParada: form.locomotivasSbParada.trim(),
            km: form.locomotivasKm.trim(),
            numeroAtivo: form.locomotivasNumeroAtivo.trim(),
            frota: form.locomotivasFrota.trim(),
            tipoOperacao: form.locomotivasTipoOperacao.trim(),
            postoOrigemSos: form.locomotivasPostoOrigemSos.trim(),
            mecanicoAcionado: form.locomotivasMecanicoAcionado.trim(),
            nivelAcionamento: form.locomotivasNivelAcionamento.trim(),
            motivo: form.locomotivasMotivo.trim(),
            observacao: form.locomotivasObservacao.trim(),
            temLiberacao: form.locomotivasTemLiberacao,
            dataLiberacao:
              form.locomotivasTemLiberacao === "sim" ?form.locomotivasDataLiberacao : "",
            horarioLiberacao: form.locomotivasHorarioLiberacao,
          },
        };
        break;
      case "VAGÕES":
        nextItem = {
          tipoInterdicao,
          sb: form.vagoesSbParada.trim(),
          sub: form.vagoesSbParada.trim() ?sbToSubMap.get(form.vagoesSbParada.trim()) || "" : "",
          kmInicial: form.vagoesKm.trim(),
          kmFinal: "",
          dataInicio: form.vagoesData,
          horaInicio: form.vagoesHorarioInicio,
          dataFim: form.vagoesTemLiberacao === "sim" ?form.vagoesDataLiberacao : "",
          horaFim: form.vagoesTemLiberacao === "sim" ?form.vagoesHorarioLiberacao : "",
          motivo: form.vagoesMotivo.trim(),
          observacao: form.vagoesObservacao.trim(),
          acionamento: `Trem: ${form.vagoesTrem.trim()} | Ativo: ${form.vagoesNumeroAtivo.trim()}`,
          nivelAcionamento: form.vagoesNivelAcionamento.trim(),
          temPrevisao: form.vagoesTemLiberacao === "sim",
          coordenada: "",
          numeroInterdicao: "",
          sbInterdicao: "",
          detalhesTipo: {
            tipo: "VAGÕES",
            data: form.vagoesData,
            horarioInicio: form.vagoesHorarioInicio,
            trem: form.vagoesTrem.trim(),
            sbParada: form.vagoesSbParada.trim(),
            km: form.vagoesKm.trim(),
            numeroAtivo: form.vagoesNumeroAtivo.trim(),
            frota: form.vagoesFrota.trim(),
            tipoOperacao: form.vagoesTipoOperacao.trim(),
            postoOrigemSos: form.vagoesPostoOrigemSos.trim(),
            mecanicoAcionado: form.vagoesMecanicoAcionado.trim(),
            nivelAcionamento: form.vagoesNivelAcionamento.trim(),
            motivo: form.vagoesMotivo.trim(),
            observacao: form.vagoesObservacao.trim(),
            temLiberacao: form.vagoesTemLiberacao,
            dataLiberacao: form.vagoesTemLiberacao === "sim" ?form.vagoesDataLiberacao : "",
            horarioLiberacao: form.vagoesHorarioLiberacao,
          },
        };
        break;
      case "VIA":
      default: {
        const temPrevisao = form.temPrevisao === "sim";

        nextItem = {
          tipoInterdicao,
          identificador: form.identificador.trim(),
          sb: form.sb.trim(),
          sub: form.sub.trim(),
          kmInicial: form.kmInicial.trim(),
          kmFinal: "",
          dataInicio: form.viaData,
          horaInicio: form.viaHorarioInicio,
          dataFim: temPrevisao ?form.dataFim : "",
          horaFim: temPrevisao ?form.horaFim : "",
          motivo: form.motivo.trim(),
          observacao: form.observacao.trim(),
          acionamento: form.acionamento.trim(),
          nivelAcionamento: form.nivelAcionamento.trim(),
          temPrevisao,
          coordenada: "",
          status: "Aguardando CCO",
          numeroInterdicao: "",
          sbInterdicao: "",
          detalhesTipo: {
            tipo: "VIA",
            data: form.viaData,
            horarioInicio: form.viaHorarioInicio,
            sb: form.sb.trim(),
            sub: form.sub.trim(),
            km: form.kmInicial.trim(),
            identificador: form.identificador.trim(),
            acionamento: form.acionamento.trim(),
            nivelAcionamento: form.nivelAcionamento.trim(),
            motivo: form.motivo.trim(),
            observacao: form.observacao.trim(),
            temPrevisao,
            dataFim: temPrevisao ?form.dataFim : "",
            horaFim: temPrevisao ?form.horaFim : "",
          },
        };
        break;
      }
    }

    try {
      setSaveError("");
      setSavingNova(true);

      const result = await cadastrarInterdicaoCim(nextItem);
      const savedItem = mapApiInterdicaoToUi(result);

      setInterdicoes((current) => {
        const next = [savedItem, ...current.filter((item) => item.id !== savedItem.id)];
        return next;
      });
      if (savedItem.dataInicio) {
        const monthRange = getMonthRange(savedItem.dataInicio);
        setFiltroInicio(monthRange.start);
        setFiltroFim(monthRange.end);
      }
      setBusca("");
      setFiltroStatus(FILTER_ALL);
      setMostrarFinalizadasAntigas(false);
      setNovaOpen(false);
      resetCadastro();
    } catch (err) {
      setSaveError(err?.message || "Não foi possível cadastrar a interdição no banco.");
    } finally {
      setSavingNova(false);
    }
  };

  const limparFiltros = () => {
    const monthRange = getMonthRange();
    setBusca("");
    setFiltroInicio(monthRange.start);
    setFiltroFim(monthRange.end);
    setFiltroTipo(FILTER_ALL);
    setFiltroStatus(FILTER_ALL);
    setFiltroMesaCco(FILTER_ALL);
    setMostrarFinalizadasAntigas(false);
  };

  return (
    <>
      <div className="interd-root">
        <aside className="interd-sidebar" ref={sidebarRef}>
          <button type="button" className="interd-sidebarBtn interd-abs interd-posMenu" onClick={onBack}>
            Menu Principal
          </button>

          <div className="interd-titleSmall interd-abs interd-posTitleData">Filtrar Data:</div>
          <div className="interd-dateRow interd-abs interd-posData">
            <InterdDateDropdown
              value={filtroInicio}
              onChange={handleFiltroInicio}
              maxDate={filtroFim}
              panelAlign="left"
              ariaLabel="Selecionar data inicial"
            />
            <InterdDateDropdown
              value={filtroFim}
              onChange={handleFiltroFim}
              minDate={filtroInicio}
              panelAlign="right"
              ariaLabel="Selecionar data final"
            />
          </div>

          <div className="interd-titleSmall interd-abs interd-posTitleBusca">Buscar:</div>
          <label className="interd-sidebarSearch interd-abs interd-posBusca">
            <input
              type="text"
              value={busca}
              onChange={(event) => setBusca(event.target.value)}
              placeholder="SB, motivo, observação, código ou identificador"
            />
          </label>

          <div className="interd-titleSmall interd-abs interd-posTitleMesaCco">Filtrar Mesa CCO:</div>
          <div className="interd-abs interd-posMesaCco">
            <InterdSingleSelect
              value={filtroMesaCco}
              onChange={setFiltroMesaCco}
              options={mesaCcoFilterOptions}
              title="Mesa CCO"
              placeholder="Todas as mesas"
              searchable
              searchPlaceholder="Buscar mesa CCO..."
            />
          </div>

          <div className="interd-titleSmall interd-abs interd-posTitleTipo">Filtrar Tipo:</div>
          <div className="interd-abs interd-posTipo">
            <InterdSingleSelect
              value={filtroTipo}
              onChange={setFiltroTipo}
              options={tipoFilterOptions}
              title="Tipo de interdição"
              placeholder="Todos os tipos"
            />
          </div>

          <div className="interd-titleSmall interd-abs interd-posTitleStatus">Filtrar Status:</div>
          <div className="interd-abs interd-posStatus">
            <InterdSingleSelect
              value={filtroStatus}
              onChange={setFiltroStatus}
              options={statusFilterOptions}
              title="Status"
              placeholder="Todos os status"
            />
          </div>

          <div className="interd-abs interd-posStatusExtra">
            <button
              type="button"
              className={`interd-summaryToggle ${mostrarFinalizadasAntigas ?"is-active" : ""}`}
              onClick={() => setMostrarFinalizadasAntigas((current) => !current)}
            >
              {mostrarFinalizadasAntigas
                ?"Ocultar finalizadas antigas"
                : `Mostrar finalizadas antigas${finalizadasAntigasOcultas ?` (${finalizadasAntigasOcultas})` : ""}`}
            </button>
          </div>

          <div className="interd-abs interd-posResumo">
            <div className="interd-sidebarSummary" ref={summaryRef}>
              <div className="interd-sidebarSummaryHead">
                <div className="interd-sidebarSummaryTitle">Resumo do período</div>
                <div className="interd-sidebarSummaryRange">{resumoSidebar.periodo}</div>
              </div>

              <div className="interd-sidebarSummaryGrid">
                <div className="interd-summaryCard">
                  <span>Total</span>
                  <strong>{resumoSidebar.total}</strong>
                </div>
                <div className="interd-summaryCard">
                  <span>Aguardando CCO</span>
                  <strong>{resumoSidebar.aguardandoCco}</strong>
                </div>
                <div className="interd-summaryCard">
                  <span>Ag. Finalização CCO</span>
                  <strong>{resumoSidebar.aguardandoFinalizacaoCco}</strong>
                </div>
                <div className="interd-summaryCard">
                  <span>Em andamento</span>
                  <strong>{resumoSidebar.emAndamento}</strong>
                </div>
                <div className="interd-summaryCard interd-summaryCard--wide">
                  <span>Finalizadas</span>
                  <strong>{resumoSidebar.finalizadas}</strong>
                </div>
              </div>
            </div>
          </div>

          <button className="interd-sidebarBtn interd-abs interd-posLimpar" type="button" onClick={limparFiltros}>
            Limpar filtros
          </button>
          <button
            className="interd-sidebarBtn interd-abs interd-posNova"
            type="button"
            onClick={() => {
              setSaveError("");
              setNovaOpen(true);
            }}
          >
            Nova Interdição
          </button>
        </aside>

        <main className="interd-content">
          <div className="interd-contentHeader">
            <div className="interd-contentHeaderCopy">
              <h2>{"Interdi\u00e7\u00f5es CIM"}</h2>
              <p>
                {
                  "Acompanhe as interdi\u00e7\u00f5es cadastradas no per\u00edodo selecionado e clique em uma linha para ver o acompanhamento."
                }
              </p>
            </div>
          </div>

          <section className="interd-board">
            {loadError ?<div className="interd-empty">{loadError}</div> : null}
            {filteredInterdicoes.length ?(
              <div className="interd-listWrap">
                <div className="interd-list">
                  {filteredInterdicoes.map((item) => renderInterdicaoListaCard(item, handleOpenDetalhes))}
                </div>
              </div>
            ) : (
              <div className="interd-empty">
                {"Nenhuma interdi\u00e7\u00e3o encontrada com os filtros atuais."}
              </div>
            )}
          </section>
        </main>
      </div>

      <InterdicaoNovaModal
        open={novaOpen}
        form={form}
        onClose={handleCloseNova}
        onSubmit={handleSubmit}
        onChange={handleFormChange}
        canSubmit={canSubmit}
        saving={savingNova}
        saveError={saveError}
        sbOptions={sbDropdownOptions}
        motivoOptionsVia={motivosViaOptions.length ?motivosViaOptions : MOTIVO_OPTIONS}
        motivoOptionsLocomotivas={motivosLocomotivasOptions}
        motivoOptionsTo={motivosToOptions}
        motivoOptionsVagoes={motivosVagoesOptions}
        tipoOperacaoOptions={tiposOperacaoOptions}
        postoOrigemSosOptions={postosOrigemOptions}
        tipoOptions={TIPO_INTERDICAO_OPTIONS}
        kmHint={kmTrabalhoHint}
        kmError={kmTrabalhoError}
      />

        <InterdicaoDetalhesModal
          open={Boolean(interdicaoDetalhes)}
          interdicao={interdicaoDetalhes}
          onClose={handleCloseDetalhes}
          onSave={handleSaveDetalhes}
          onFinalize={handleFinalizarDetalhes}
          saving={savingDetalhes}
          savingAction={savingDetalhesAction}
          saveError={saveDetalhesError}
        />
    </>
  );
}

